/**
 * @fileoverview Deactivate User form.
 * @description The Deactivate dialog's form for one version of the user's details: what
 * deactivating does, then the reason and note. Presentational: the form logic is in
 * useDeactivateUserForm.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import LifecycleReasonFields from '@/features/users/components/LifecycleReasonFields'
import {
  DEACTIVATE_FORM_ID,
  DEACTIVATE_TEST_ID,
} from '@/features/users/constants/lifecycle.constants'
import { useDeactivateUserForm } from '@/features/users/hooks/useDeactivateUserForm'

import type {
  DeactivateUserRequest,
  LifecycleReason,
  UserDetail,
} from '@/features/users/types/users.types'

interface DeactivateUserFormProps {
  user: UserDetail
  hasReports: boolean
  reasons: LifecycleReason[]
  noteMaxLength: number
  onSubmit: (payload: DeactivateUserRequest) => void
}

/**
 * @description Renders what deactivating this user does.
 * @param {{ user: UserDetail; hasReports: boolean }} props - The user being deactivated, and
 * whether anyone reports to them (the note about their reports moving only shows when someone
 * would move).
 */
function ConsequenceNote({ user, hasReports }: { user: UserDetail; hasReports: boolean }) {
  const { t } = useTranslation()
  const manager = user.reportingManager?.displayName

  return (
    <div
      className="flex flex-col gap-1.5 rounded-lg border border-input-border bg-input-bg px-3.5 py-2.5 text-[13px] text-ink"
      data-testid={`${DEACTIVATE_TEST_ID}-note`}
    >
      <p>{t('sales.userManagement.deactivateUser.signedOut', { name: user.displayName })}</p>
      {hasReports && (
        <p data-testid={`${DEACTIVATE_TEST_ID}-reports-note`}>
          {manager
            ? t('sales.userManagement.deactivateUser.reportsMoveTo', { manager })
            : t('sales.userManagement.deactivateUser.reportsMoveUp')}
        </p>
      )}
      {user.directoryStatus === 'PENDING' && (
        <p data-testid={`${DEACTIVATE_TEST_ID}-invitation-note`}>
          {t('sales.userManagement.deactivateUser.invitationRevoked')}
        </p>
      )}
    </div>
  )
}

/**
 * @component
 * @param {DeactivateUserFormProps} props - The user, whether anyone reports to them, the reasons to
 * choose from, the note limit, and what to do with the chosen reason.
 * @param {UserDetail} props.user - The user being deactivated.
 * @param {boolean} props.hasReports - Whether anyone would move to the user's manager.
 * @param {LifecycleReason[]} props.reasons - The reasons to choose from.
 * @param {number} props.noteMaxLength - The longest the note may be.
 * @param {(payload: DeactivateUserRequest) => void} props.onSubmit - Called with the request once
 * the form is valid.
 */
function DeactivateUserForm({
  user,
  hasReports,
  reasons,
  noteMaxLength,
  onSubmit: submitReason,
}: DeactivateUserFormProps) {
  const { form, noteRequired, reasonError, noteError, onSubmit } = useDeactivateUserForm(
    noteMaxLength,
    submitReason,
  )
  const { register } = form

  return (
    <form
      id={DEACTIVATE_FORM_ID}
      data-testid={`${DEACTIVATE_TEST_ID}-form`}
      onSubmit={onSubmit}
      noValidate
      className="flex flex-col gap-4"
    >
      <ConsequenceNote user={user} hasReports={hasReports} />
      <LifecycleReasonFields
        idPrefix="deactivateUser"
        testIdPrefix={DEACTIVATE_TEST_ID}
        reasons={reasons}
        reasonRegistration={register('reasonCode')}
        noteRegistration={register('reasonNote')}
        noteRequired={noteRequired}
        noteMaxLength={noteMaxLength}
        reasonError={reasonError}
        noteError={noteError}
      />
    </form>
  )
}

export default DeactivateUserForm
